import { Button, Card, CardContent, Divider, Grid, MenuItem, Chip as MuiChip, TablePagination, TextField } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { Chip } from 'components/chips'
import { ListFilterGroup } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { VehicleToDoListParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'

export default function NeedsClusterConversionInHouseToDoListDetails() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [loadingState, setLoadingState] = useLoadingState({ isGettingVehicles: false })
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    sortBy: 'clusterPulledDate,exporterBuyer',
    sortDirection: 'asc'
  })
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const getNeedsClusterConversionInHouseToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.toDoLists
      .getNeedsClusterConversionInHouseToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Needs Cluster Conversion In House To Do List')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingVehicles)
  }, [layout, loadingState.isGettingVehicles])

  useEffect(() => {
    getNeedsClusterConversionInHouseToDoList(parameters)
  }, [getNeedsClusterConversionInHouseToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  const isDateExceedingLimit = (date: string | Date, limit: number) => {
    if (!date || isNaN(new Date(date).getTime())) return false
    return moment().diff(moment(date), 'days') >= limit
  }

  const formatDateToMMDDYYYY = (date: string | Date | undefined): string => {
    if (!date || isNaN(new Date(date).getTime())) return ''
    return formatDate(date)
  }

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm='auto' alignContent='center'>
              <Grid item container xs={12} justifyContent='space-between'>
                <ListFilterGroup>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    margin='dense'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    size='small'
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='vehicleCheckInDate'>Vehicle Check-In Date</MenuItem>

                    <MenuItem value='clusterPulledDate'>Cluster Pulled Date</MenuItem>

                    <MenuItem value='exporterBuyer'>Exporter/Buyer</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock#</MenuItem>

                    <MenuItem value='clusterConversionStarted'>Cluster Conversion Started</MenuItem>
                  </TextField>

                  {['', 'exporterBuyer', 'vin', 'year', 'make', 'model', 'importerStockNumber'].includes(filterField) && (
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        disabled={!filterField}
                        label='Filter Value'
                        margin='dense'
                        onChange={e => {
                          setFilterValue(e.target.value)
                        }}
                        size='small'
                        value={filterValue || ''}
                      />
                    </Grid>
                  )}

                  {filterField === 'vehicleCheckInDate' && (
                    <>
                      <Grid container>
                        <Grid item xs={12} sm='auto'>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                            label='Filter Date From'
                            onChange={e => {
                              setFilterValueFrom(e.target.value)
                            }}
                            type='date'
                            value={filterValueFrom}
                          />
                        </Grid>

                        <Grid item xs={12} sm='auto'>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                            label='Filter Date To'
                            onChange={e => {
                              setFilterValueTo(e.target.value)
                            }}
                            type='date'
                            value={filterValueTo}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {filterField === 'clusterPulledDate' && (
                    <>
                      <Grid container>
                        <Grid item xs={12} sm='auto'>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                            label='Filter Date From'
                            onChange={e => {
                              setFilterValueFrom(e.target.value)
                            }}
                            type='date'
                            value={filterValueFrom}
                          />
                        </Grid>

                        <Grid item xs={12} sm='auto'>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                            label='Filter Date To'
                            onChange={e => {
                              setFilterValueTo(e.target.value)
                            }}
                            type='date'
                            value={filterValueTo}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {filterField === 'clusterConversionStarted' && (
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        fullWidth
                        label='Filter Value'
                        onChange={e => {
                          setFilterValue(e.target.value)
                        }}
                        select
                        sx={{ minWidth: 200 }}
                        value={filterValue || ''}
                      >
                        <MenuItem value='true'>
                          <Chip variant='green'>Yes</Chip>
                        </MenuItem>

                        <MenuItem value='false'>
                          <Chip variant='red'>No</Chip>
                        </MenuItem>
                      </TextField>
                    </Grid>
                  )}

                  <Grid item xs={12} sm='auto'>
                    <Button
                      color='inherit'
                      disabled={
                        !filterField ||
                        (filterField === 'vehicleCheckInDate' ? !filterValueFrom || !filterValueTo : !filterValue) ||
                        (filterField === 'clusterPulledDate' ? !filterValueFrom || !filterValueTo : !filterValue)
                      }
                      variant='contained'
                      onClick={() => {
                        if (filterField === 'vehicleCheckInDate') {
                          setParameters({
                            ...parameters,
                            vehicleCheckInDateFrom: filterValueFrom,
                            vehicleCheckInDateTo: filterValueTo
                          })
                        } else if (filterField === 'clusterPulledDate') {
                          setParameters({
                            ...parameters,
                            clusterPulledDateFrom: filterValueFrom,
                            clusterPulledDateTo: filterValueTo
                          })
                        } else if (filterField === 'exporterBuyer') {
                          setParameters({
                            ...parameters,
                            exporter: filterValue,
                            buyer: filterValue
                          })
                        } else {
                          setParameters({
                            ...parameters,
                            [filterField]: filterValue
                          })
                        }

                        setFilterField('')
                        setFilterValue('')
                        setFilterValueFrom('')
                        setFilterValueTo('')
                      }}
                    >
                      APPLY FILTER
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <Button
                      color='secondary'
                      disabled={
                        !Boolean(
                          parameters.vehicleCheckInDateTo ||
                            parameters.vehicleCheckInDateFrom ||
                            parameters.clusterPulledDateTo ||
                            parameters.clusterPulledDateFrom ||
                            parameters.exporter ||
                            parameters.buyer ||
                            parameters.vin ||
                            parameters.year ||
                            parameters.make ||
                            parameters.model ||
                            parameters.importerStockNumber ||
                            parameters.clusterConversionStarted
                        )
                      }
                      variant='text'
                      onClick={() => {
                        setParameters({
                          ...parameters,
                          vehicleCheckInDateTo: 'undefined',
                          vehicleCheckInDateFrom: 'undefined',
                          clusterPulledDateTo: 'undefined',
                          clusterPulledDateFrom: 'undefined',
                          exporter: 'undefined',
                          buyer: 'undefined',
                          vin: 'undefined',
                          year: undefined,
                          make: 'undefined',
                          model: 'undefined',
                          importerStockNumber: 'undefined',
                          clusterConversionStarted: undefined
                        })
                      }}
                    >
                      RESET FILTERS
                    </Button>
                  </Grid>
                </ListFilterGroup>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {parameters.vehicleCheckInDateFrom && parameters.vehicleCheckInDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Check-In Date from "${moment(parameters.vehicleCheckInDateFrom).format(
                      'M/D/yyyy'
                    )}" to "${moment(parameters.vehicleCheckInDateTo).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vehicleCheckInDateFrom: undefined,
                        vehicleCheckInDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.clusterPulledDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Cluster Pulled Date from "${moment(parameters.clusterPulledDateFrom).format(
                      'M/D/yyyy'
                    )}" to "${moment(parameters.clusterPulledDateTo).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        clusterPulledDateFrom: undefined,
                        clusterPulledDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {(parameters.exporter || parameters.buyer) && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter/Buyer Contains "${parameters.exporter || parameters.buyer}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        exporter: undefined,
                        buyer: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN Contains "${parameters.vin}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vin: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year Contains "${parameters.year}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        year: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make Contains "${parameters.make}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        make: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model Contains "${parameters.model}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        model: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock# Contains "${parameters.importerStockNumber}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        importerStockNumber: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.clusterConversionStarted && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Cluster Conversion Started Status is "${
                      parameters.clusterConversionStarted.toString() === 'true' ? 'Yes' : 'No'
                    }"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        clusterConversionStarted: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {vehicles?.map(vehicle => (
        <Card sx={{ mb: 2, mt: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12}>
                <Grid container spacing={1} alignItems='flex-start'>
                  <Grid item xs={12}>
                    <LabelField
                      label='Vehicle Check-In Date'
                      value={formatDateToMMDDYYYY(vehicle?.vehicleCheckInDateTime)}
                      error={!!vehicle.vehicleCheckInDateTime && isDateExceedingLimit(vehicle.vehicleCheckInDateTime, 4)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Cluster Pulled Date'
                      value={formatDateToMMDDYYYY(vehicle.clusterPulledDateTime)}
                      error={!!vehicle.clusterPulledDateTime && isDateExceedingLimit(vehicle.clusterPulledDateTime, 2)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Exporter/Buyer'
                      value={vehicle?.buyerCompany?.name ? vehicle.buyerCompany.name : vehicle?.exporterCompany?.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='VIN' value={vehicle.vin} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Year' value={vehicle.year} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Make' value={vehicle.make} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Model' value={vehicle.model} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Importer Stock #' value={vehicle.importerStockNumber} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Cluster Conversion Started'
                      value={vehicle?.clusterConversionInHouseStarted ? 'Yes' : 'No'}
                    />
                  </Grid>

                  <Grid item xs={12} container justifyContent='flex-end' alignItems='center'>
                    <Button
                      onClick={() => {
                        navigate('/vehicles/' + vehicle.vehicleGuid + '/needs-cluster-conversion-in-house/')
                      }}
                      variant='contained'
                    >
                      SCAN CLUSTER
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Grid item xs={12} marginTop={1}>
        <ExplanationAccordion>
          || Vehicles appear in this to do list if Vehicle Check-In = Complete, Convert Cluster = Yes, Pulled Cluster Photo
          Uploaded = Yes, 3rd Party Cluster Convert = No, and Cluster Conversion Completed = No. Note that “Cluster Conversion
          Started” could be Yes or No and the vehicle would still appear in this list. || Cluster Conversion Started = Yes if a
          user pressed “Start Cluster Conversion” for this vehicle’s cluster. || The “Needs Cluster Conversion In-House” To Do
          List card’s “Vehicle Checked-In Date” value will be in bold and red if current date is ≥ Vehicle Checked-In Date + 4
          days. “Vehicle Checked-In Date” is the date the first vehicle check-in image was associated with the vehicle. || The
          “Needs Cluster Conversion In-House” To Do List card’s “Cluster Pulled Date” value will be in bold and red if current
          date is ≥ Cluster Pulled Date + 2 days. “Cluster Pulled Date” is the date the “Pulled Cluster Photo” was associated
          with the vehicle. || The “Needs Cluster Conversion In-House” To Do List card’s “Exporter/Buyer” field displays the
          name of the Buyer company associated with the vehicle if one exists, else the name of the Exporter company. || The
          “Needs Cluster Conversion In-House” To Do List cards are sorted by “Cluster Pulled Date” oldest to newest and then by
          Exporter/Buyer alphabetically. || The Needs Cluster Conversion In-House “To Do List” is a card screen intended for
          users on a phone. ||
        </ExplanationAccordion>
      </Grid>

      <Grid item xs={12}>
        <TablePagination
          component='div'
          count={totalCount}
          onPageChange={(_, page: number) => {
            setParameters({
              ...parameters,
              page
            })
          }}
          onRowsPerPageChange={e => {
            setParameters({
              ...parameters,
              page: 0,
              pageSize: e.target.value as unknown as number
            })
          }}
          page={parameters.page}
          rowsPerPage={parameters.pageSize}
          labelRowsPerPage={
            <Grid container spacing={1}>
              <Grid item>Order By:</Grid>

              <Grid item>
                <TextField
                  InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                  onChange={e => {
                    setParameters({ ...parameters, orderBy: e.target.value })
                  }}
                  select
                  size='small'
                  value={parameters.orderBy}
                  variant='standard'
                >
                  <MenuItem value='vehicleCheckInDate'>Vehicle Check-In Date</MenuItem>

                  <MenuItem value='clusterPulledDate'>Cluster Pulled Date</MenuItem>

                  <MenuItem value='exporterBuyer'>Exporter/Buyer</MenuItem>

                  <MenuItem value='vin'>VIN</MenuItem>

                  <MenuItem value='year'>Year</MenuItem>

                  <MenuItem value='make'>Make</MenuItem>

                  <MenuItem value='model'>Model</MenuItem>

                  <MenuItem value='importerStockNumber'>Importer Stock#</MenuItem>

                  <MenuItem value='clusterConversionStarted'>Cluster Conversion Started</MenuItem>
                </TextField>
              </Grid>

              <Grid item>
                <TextField
                  InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                  onChange={e => {
                    setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                  }}
                  select
                  size='small'
                  value={parameters.order}
                  variant='standard'
                >
                  <MenuItem value='asc'>Ascending</MenuItem>

                  <MenuItem value='desc'>Descending</MenuItem>
                </TextField>
              </Grid>

              <Grid item sx={{ alignSelf: 'stretch' }}>
                <Divider orientation='vertical' />
              </Grid>

              <Grid item>Cards Per Page:</Grid>
            </Grid>
          }
          rowsPerPageOptions={[
            { label: '5', value: 5 },
            { label: '10', value: 10 },
            { label: '25', value: 25 },
            { label: '50', value: 50 },
            { label: '100', value: 100 }
          ]}
          SelectProps={{ fullWidth: false }}
        />
      </Grid>
    </>
  )
}
